import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { Table } from "../../../../../components/table/Table";
import { api } from "../../../../../services/api/api";
import { Chip, IconButton, Typography, Tooltip, Spinner } from "@material-tailwind/react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { OdooHistoryItem } from "../../../../../types/odoo/odooHistory";
import toast from "react-hot-toast";
import SendToOdooDialog from "../../../../../components/workorders/SendToOdooDialog";
import DestockDialog from "../../../../../components/workorders/DestockDialog";
import { colors } from "@material-tailwind/react/types/generic";
import { useWorkorderContext } from "../../../../../context/WorkorderContext";
import { useOdooConfig } from "../../../../../hooks/useOdooConfig";
import {
  canRetryOdooHistoryItem,
  isOdooStockAction,
} from "../../../../../services/odoo/workorderOdoo";

interface OdooHistoryTabProps {
  workorder: EasyTech.Workorder | null;
  history?: OdooHistoryItem[];
  onRefresh?: () => Promise<void>;
}

const date = {
  formatDate: (d: number) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString();
  },
  formatTime: (d: number) => {
    if (!d) return "";
    return new Date(d).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
};

const ODOO_SALES_URL = "https://chrono-flex-international.odoo.com/" + "odoo/physical-inventory";

const OdooHistoryTab: React.FC<OdooHistoryTabProps> = ({ workorder, history: propHistory, onRefresh }) => {
  const [internalHistory, setInternalHistory] = useState<OdooHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [retryLoading, setRetryLoading] = useState<string | null>(null);
  const { config } = useOdooConfig();

  const history = propHistory || internalHistory;

  // États pour les dialogues
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showDestockDialog, setShowDestockDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OdooHistoryItem | null>(null);

  const { updateWorkorder } = useWorkorderContext();

  useEffect(() => {
    if (workorder?.id && !propHistory) {
      fetchHistory();
    }
  }, [workorder?.id, propHistory]);

  const fetchHistory = async () => {
    if (onRefresh) {
      setLoading(true);
      await onRefresh();
      setLoading(false);
      return;
    }

    console.log("fetch history");
    try {
      setLoading(true);
      // Add timestamp to prevent caching
      const data = await api.get(`admin/odoo/history/${workorder?.id}?t=${new Date().getTime()}`);
      console.log("data", data);
      if (Array.isArray(data)) {
        // Sort by creation date descending (newest first)
        setInternalHistory(data.sort((a: any, b: any) => b.created - a.created));
      }
    } catch (error) {
      console.error("Error fetching odoo history:", error);
    } finally {
      setLoading(false);
      console.log("history fetched", history);
    }
  };

  const getLatestActionIds = () => {
    // On trie par date de création descendante pour être sûr d'avoir le plus récent en premier
    const sortedHistory = [...history].sort((a, b) => b.created - a.created);

    const latestSend = sortedHistory.find(h => h.action === "SEND");
    const latestDestock = sortedHistory.find(h => isOdooStockAction(h.action));

    return {
      send: latestSend?.id,
      destock: latestDestock?.id
    };
  };

  const latestActions = getLatestActionIds();

  const handleRetry = (item: OdooHistoryItem) => {
    if (retryLoading) return;
    setSelectedItem(item);

    if (item.action === "SEND") {
      setShowSendDialog(true);
    } else if (isOdooStockAction(item.action)) {
      setShowDestockDialog(true);
    }
  };

  const confirmRetry = async () => {
    if (!selectedItem || !workorder) return;

    try {
      setRetryLoading(selectedItem.id);

      if (selectedItem.action === "SEND") {
        await api.request("POST", `odoo/order/${workorder.id}`);
        toast.success(t("workorder:odoo:sendedToOdoo"));
        setShowSendDialog(false);
      } else if (isOdooStockAction(selectedItem.action)) {
        await api.request("POST", `odoo/order/destock/${workorder.id}`);
        toast.success(t("workorder:logs:destocked"));
        setShowDestockDialog(false);

        // Update workorder optimism
        if (workorder.invoice) {
          const updatedWorkorder = {
            ...workorder,
            invoice: {
              ...workorder.invoice,
              stock: {
                ...(workorder.invoice.stock || {}),
                status: 'SUCCESS'
              }
            }
          };
          updateWorkorder(updatedWorkorder as EasyTech.Workorder);
        }
      }
    } catch (error) {
      console.error("Error retrying action:", error);
      if (selectedItem.action === "SEND") {
        toast.error(t("workorder:odoo:errorSendToOdoo"));
      } else if (isOdooStockAction(selectedItem.action)) {
        toast.error(t("workorder:status:destock_error"));
      }
    } finally {
      setRetryLoading(null);
      setSelectedItem(null);
      setShowSendDialog(false);
      setShowDestockDialog(false);
      // Refresh history immediately after action completes
      await fetchHistory();
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case "SEND":
        return t("workorder:odoo:history:send");
      case "STOCK":
      case "DESTOCK":
        return t("workorder:odoo:history:stock");
      default:
        return action;
    }
  };

  const getStatusColorText = (status: string): { color: colors, text: string } => {
    switch (status) {
      case "SUCCESS":
        return {
          color: "green",
          text: t("workorder:odoo:history:status:success")
        };
      case "ERROR":
        return {
          color: "red",
          text: t("workorder:odoo:history:status:error")
        };
      case "ERROR_RETRY":
        return {
          color: "red",
          text: t("workorder:odoo:history:status:errorRetry")
        };
      case "PENDING":
        return {
          color: "orange",
          text: t("workorder:odoo:history:status:pending")
        };
      default:
        return {
          color: "blue-gray",
          text: t("workorder:odoo:history:status:default")
        };
    }
  };

  if (!workorder) return null;

  const tableHead = [
    { label: t("workorder:odoo:history:action"), orderKey: "action" },
    { label: t("workorder:odoo:history:date"), orderKey: "created" },
    { label: t("workorder:odoo:history:author"), orderKey: "user.lastName" },
    { label: t("workorder:odoo:history:status.label"), orderKey: "status" },
    { label: t("workorder:odoo:history:detail"), orderKey: "message" },
    { label: t("workorder:odoo:history:actions"), orderKey: null },
  ];

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4 border-b border-gray-300 pb-4">
        <h2 className="text-xl font-medium text-gray-900">
          {t("workorder:tabs:odooHistory")}
        </h2>
      </div>

      <Table
        tableHead={tableHead}
        emptyTable={t("workorder:noData")}
      >
        <></>
        {history.map((item, index) => (
          <tr key={index} className="border-b border-gray-300 last:border-0 hover:bg-gray-50 transition-colors">
            <td className="p-4 whitespace-nowrap md:whitespace-normal">
              <Typography variant="small" color="blue-gray" className="font-normal break-words text-xs md:text-sm">
                {getActionLabel(item.action)}
              </Typography>
            </td>
            <td className="p-4 whitespace-nowrap md:whitespace-normal">
              <Typography variant="small" color="blue-gray" className="font-normal text-xs md:text-sm">
                {date.formatDate(item.created)} {date.formatTime(item.created)}
              </Typography>
            </td>
            <td className="p-4 whitespace-nowrap md:whitespace-normal">
              <Typography variant="small" color="blue-gray" className="font-normal text-xs md:text-sm">
                {item.user ? `${item.user.firstName} ${item.user.lastName}` : "-"}
              </Typography>
            </td>
            <td className="p-4 whitespace-nowrap">
              <Chip
                variant="ghost"
                size="sm"
                value={getStatusColorText(item.status).text}
                className="w-fit h-fit rounded-full py-1 capitalize text-[10px] md:text-xs"
                color={getStatusColorText(item.status).color}
              />
            </td>
            <td className="p-4 min-w-[120px] md:min-w-[300px] whitespace-normal break-words">
              {(() => {
                // Gestion spécifique pour les listes de produits (stock ou produit non trouvé ou en attente de validation)
                if (
                  item.extras &&
                  item.extras.some((extra) => extra.key === "PRODUCT") &&
                  (item.code === "workorder:odoo:product:notFound" ||
                    item.code === "workorder:odoo:stock:notFound" ||
                    item.code === "workorder:odoo:product:awaitingValidation")
                ) {
                  const productsByCode = item.extras
                    .filter((extra) => extra.key === "PRODUCT")
                    .reduce((acc, extra) => {
                      // Utiliser le code de l'extra s'il existe, sinon le code parent
                      const code = extra.code || item.code;
                      if (!code) return acc;
                      if (!acc[code]) {
                        acc[code] = [];
                      }
                      acc[code].push(extra.value);
                      return acc;
                    }, {} as Record<string, string[]>);

                  const codes = Object.keys(productsByCode);

                  if (codes.length > 0) {
                    return (
                      <div className="flex flex-col gap-4 break-words whitespace-normal w-full">
                        {codes.map((code, idx) => (
                          <div key={idx} className="flex flex-col gap-1 w-full">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-normal text-xs md:text-sm font-semibold"
                            >
                              {t(code, { list: "" })}
                            </Typography>
                            <ul className="list-disc list-inside pl-4 w-full">
                              {productsByCode[code].map((product, pIdx) => (
                                <li key={pIdx} className="break-all whitespace-normal">
                                  <Typography
                                    variant="small"
                                    color="blue-gray"
                                    className="font-normal inline break-all whitespace-normal text-xs md:text-sm"
                                  >
                                    {product}
                                  </Typography>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    );
                  }
                }

                // Gestion spécifique pour le message de succès de déstockage avec lien
                if (item.code === "workorder:odoo:destock") {
                  const text = t(item.code);
                  const parts = text.split(":");

                  if (parts.length > 1) {
                    return (
                      <Tooltip content={text} className="max-w-[400px] z-[9999]">
                        <div className="w-full !whitespace-normal">
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-normal break-words whitespace-pre-wrap text-xs md:text-sm"
                          >
                            {parts[0].trim()}:
                            <a
                              href={config?.odooHost ? `${config.odooHost}/odoo/physical-inventory` : (config?.easytechUrl ? `${config.easytechUrl}/odoo/physical-inventory` : ODOO_SALES_URL)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline ml-1 font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {parts.slice(1).join(":").trim()}
                            </a>
                          </Typography>
                        </div>
                      </Tooltip>
                    );
                  }
                }

                // Cas standard (texte simple)
                const detailText = item.code
                  ? (() => {
                    // Si c'est le code pour une facture créée, on extrait le numéro depuis extras
                    if (
                      item.code === "workorder:odoo:invoice:created" &&
                      item.extras
                    ) {
                      const invoiceExtra = item.extras.find(
                        (extra) => extra.key === "INVOICE"
                      );
                      if (invoiceExtra?.value) {
                        return t(item.code, {
                          invoiceNumber: invoiceExtra.value,
                        });
                      }
                    }
                    return t(item.code);
                  })()
                  : item.message;

                return (
                  <Tooltip content={detailText} className="max-w-[400px] z-[9999]">
                    <div className="w-full !whitespace-normal">
                      <Typography
                        variant="small"
                        color="blue-gray"
                        className="font-normal cursor-help break-words whitespace-pre-wrap text-xs md:text-sm"
                      >
                        {detailText}
                      </Typography>
                    </div>
                  </Tooltip>
                );
              })()}
            </td>
            <td className="p-4 whitespace-nowrap">
              {(() => {
                const canRetry = canRetryOdooHistoryItem({
                  item,
                  latestActions,
                  workorder,
                });

                if (!canRetry) {
                  return null;
                }

                return (
                  <Tooltip content={t("common:retry")}>
                    <span>
                      <IconButton
                        variant="text"
                        color="blue"
                        onClick={() => handleRetry(item)}
                        disabled={retryLoading === item.id}
                        className="hover:bg-blue-50"
                        size="sm"
                      >
                        {retryLoading === item.id ? (
                          <Spinner className="h-4 w-4" color="blue" />
                        ) : (
                          <ArrowPathIcon
                            className="h-4 w-4"
                            strokeWidth={2}
                            style={{ color: "#1464E1" }}
                          />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                );
              })()}
            </td>
          </tr>
        ))}
      </Table>

      {/* Dialogues de confirmation */}
      <SendToOdooDialog
        open={showSendDialog}
        workorderRef={workorder.ref}
        onClose={() => {
          setShowSendDialog(false);
          setSelectedItem(null);
        }}
        onConfirm={confirmRetry}
        loading={!!retryLoading && selectedItem?.action === "SEND"}
      />

      <DestockDialog
        open={showDestockDialog}
        workorderRef={workorder.ref}
        onClose={() => {
          setShowDestockDialog(false);
          setSelectedItem(null);
        }}
        onConfirm={confirmRetry}
        loading={!!retryLoading && !!selectedItem && isOdooStockAction(selectedItem.action)}
      />
    </div>
  );
};

export default OdooHistoryTab;
